import type {
  RESTRICTION_MODEL_CONTEXTS,
  RESTRICTION_TYPES,
} from 'lumen/utils/constants/channel-pricing-constants';

// SMART DISTRIBUTION TODO: Update "pricing-assistant-constants" to use these Abstract Actions.
//                          Discuss with the team about approaches to make Action types agnostic for other products
/**
 * Represents the possible Actions status available in the BE.
 *
 * It should be synced to the BE definitions in AbstractBaseRecommendationAction.Action.
 * Reference:
 *  `https://git.otainsight.com/ota-insight-repos/ota-insight-platform/ota-insight-api/usermanagement/-/blob/HEAD/recommendations/models/action.py#L14`
 */
export const ABSTRACT_ACTIONS = {
  ACCEPTED: 'accepted',
  SNOOZED: 'snoozed',
  LONG_SNOOZED: 'long_snoozed',
  PUSHING: 'pushing',
  PUSHED: 'pushed',
  EXCEPTION: 'exception',
  INVALIDATED: 'invalidated',
} as const;

export type AbstractActionValue =
  (typeof ABSTRACT_ACTIONS)[keyof typeof ABSTRACT_ACTIONS];

/** Represents the basic properties required by Actions */
interface ActionBase<Value, Context> {
  action: AbstractActionValue;
  channel_id?: string;
  context: Context;
  current_values?: Value[];
  from_date: string;
  rate_group_id?: string | null;
  rate_id?: string;
  room_id?: string;
  source?: string;
  values: Value[];
}

/** Represents the properties required for Creating Actions via POST requests */
export interface PostActionPayload<Value, Context> extends ActionBase<
  Value,
  Context
> {
  factors?: object;
  subscription: number;
}

/**
 * SPECIALIZATION FOR RESTRICTION ACTIONS
 * Below we have the implementation of the agnostic Actions for the Restrictions space
 */

/** Represents contexts for Restriction Actions */
export type RestrictionContext =
  (typeof RESTRICTION_MODEL_CONTEXTS)[keyof typeof RESTRICTION_MODEL_CONTEXTS];

/**
 * Couples a context with its rate-group identifier: a RATE_GROUP_LEVEL action
 * must carry a rateGroupId, and any other context must not. Prevents building a
 * payload whose context is RATE_GROUP_LEVEL while silently omitting rate_group_id.
 */
export type RateGroupContextFields =
  | {
      context: (typeof RESTRICTION_MODEL_CONTEXTS)['RATE_GROUP_LEVEL'];
      rateGroupId: string;
    }
  | {
      context: Exclude<
        RestrictionContext,
        (typeof RESTRICTION_MODEL_CONTEXTS)['RATE_GROUP_LEVEL']
      >;
      rateGroupId?: never;
    };

/** Represent types for Restriction Actions */
export type RestrictionType =
  (typeof RESTRICTION_TYPES)[keyof typeof RESTRICTION_TYPES];

/** Represent value objects for Restriction Actions */
export type RestrictionActionValue = { type: RestrictionType; value?: number };

/** Possible contexts for Restriction Actions */
export type MinimalRestrictionAction = {
  context: RestrictionContext;
  from_date: string;
  rate_group_id?: string | null;
  values: RestrictionActionValue[];
};

/** Represent Restriction Actions parameters required for creation (POST request) */
export type RestrictionActionPostPayload = PostActionPayload<
  RestrictionActionValue,
  RestrictionContext
>;
